import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { useAuth } from "@/auth/AuthContext";
import { newestFirst } from "@/utils/sortHistory";
import BoardWorkspace from "@/components/BoardWorkspace";
import { InformationalStatusText, formatInformationalStatus } from "@/components/InformationalStatusText";
import { TypedDateInput } from "@/components/TypedDateInput";
import { LocateReadinessLabel, PermitReadinessLabel, todayISO } from "@/components/PermitCalculatedBadge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/operations`;
const LOCATE_CONDITIONS = ["valid", "trouble", "remark"];
const emptyForm = {
  project_id: "",
  permit_type: "Permit",
  reference_number: "",
  request_date: "",
  expires_on: "",
  received_at: "",
  received_at_source: "",
  does_not_expire: false,
  status: "missing",
  notes: "",
  ticket_not_needed: false,
  ticket_description: "",
  active: true,
};

const newestRecordsFirst = newestFirst;

export default function PermitsLocatesPage() {
  const { canManageRecords } = useAuth();
  const [searchParams] = useSearchParams();
  const targetPermitId = searchParams.get("permit_id") || "";
  const [activeRecords, setActiveRecords] = useState([]);
  const [historyRecords, setHistoryRecords] = useState([]);
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState("");
  const load = useCallback(async () => {
    const [open, history] = await Promise.all([
      axios.get(`${API}/permits-locates`, { params: { filter: "open" } }),
      axios.get(`${API}/permits-locates`, { params: { filter: "history" } }),
    ]);
    setActiveRecords(open.data.records || []);
    setHistoryRecords(history.data.records || []);
  }, []);
  const loadProjects = useCallback(async () => {
    const response = await axios.get(`${API}/bootstrap`);
    setProjects(response.data.projects || []);
  }, []);
  useEffect(() => {
    load().catch(() => toast.error("Permits and locates could not be loaded."));
  }, [load]);
  useEffect(() => {
    loadProjects().catch(() => toast.error("Projects could not be loaded."));
  }, [loadProjects]);
  useEffect(() => {
    if (!targetPermitId) {
      return;
    }
    const target = [...activeRecords, ...historyRecords].find(
      (record) => record.id === targetPermitId,
    );
    if (!target) {
      return;
    }
    if (canManageRecords && editingId !== target.id) {
      setEditingId(target.id);
      setForm({ ...target });
    }
    window.requestAnimationFrame(() => {
      document
        .querySelector(`[data-testid="permit-record-${targetPermitId}"], [data-testid="permit-history-record-${targetPermitId}"]`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }, [activeRecords, canManageRecords, editingId, historyRecords, targetPermitId]);
  const save = async (event) => {
    event.preventDefault();
    const isPermitType = form.permit_type === "Permit";
    // Permit: humans record the received fact; JFM calculates status. 811: keep condition.
    const conditionFields = isPermitType
      ? { received_at: form.received_at, does_not_expire: form.does_not_expire, expires_on: form.does_not_expire ? "" : form.expires_on }
      : { status: form.status };
    try {
      if (editingId)
        await axios.patch(`${API}/permits/${editingId}`, {
          permit_type: form.permit_type,
          reference_number: form.reference_number,
          request_date: form.request_date,
          expires_on: form.expires_on,
          ...conditionFields,
          notes: form.notes,
          ticket_not_needed: form.ticket_not_needed,
          ticket_description: form.ticket_description,
          active: form.active,
        });
      else await axios.post(`${API}/permits`, { ...form, ...conditionFields });
      setForm(emptyForm);
      setEditingId("");
      await load();
      toast.success("Permit / locate saved.");
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Permit / locate could not be saved.",
      );
    }
  };
  const edit = (record) => {
    setEditingId(record.id);
    setForm({ ...record });
  };
  const startEntry = (permitType) => {
    setEditingId("");
    setForm({ ...emptyForm, permit_type: permitType });
  };
  const close = async (record) => {
    try {
      await axios.post(`${API}/permits/${record.id}/deactivate`);
      await load();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Record could not be closed.",
      );
    }
  };
  return (
    <BoardWorkspace
      eyebrow="Office permit & locate control"
      headingTestId="permits-locates-heading"
      subtitle="Multiple Permit and Locate / 811 records can remain active for a project. Company Owner/Admin and Office/Admin control edits; all company users can view the operational queue."
      title="Permits & Locates"
    >
      <section
        className="project-notes-panel"
        data-testid="permits-locates-workspace"
      >
        {canManageRecords && (
          <div className="communication-filters permit-entry-actions" data-testid="permit-locate-entry-actions">
            <button data-testid="add-permit-action" onClick={() => startEntry("Permit")} type="button">Add Permit</button>
            <button data-testid="add-locate-action" onClick={() => startEntry("Locate / 811")} type="button">Add Locate / 811</button>
          </div>
        )}
        {canManageRecords && (
          <form
            className="field-note-form"
            data-testid="permit-locate-form"
            onSubmit={save}
          >
            <p className="permit-entry-type" data-testid="permit-entry-type">
              {editingId ? `Editing ${form.permit_type}` : `New ${form.permit_type}`}
            </p>
            <select
              data-testid="permit-project-select"
              onChange={(e) => setForm({ ...form, project_id: e.target.value })}
              required
              value={form.project_id}
            >
              <option value="">Select project</option>
              {projects.map((project) => {
                const projectLabel =
                  project.project_code === project.name
                    ? project.project_code
                    : `${project.project_code} · ${project.name}`;
                return (
                  <option key={project.id} value={project.id}>
                    {projectLabel}
                  </option>
                );
              })}
            </select>
            <input
              data-testid="permit-reference-number"
              onChange={(e) =>
                setForm({ ...form, reference_number: e.target.value })
              }
              placeholder="Reference / ticket number"
              value={form.reference_number}
            />
            <TypedDateInput
              data-testid="permit-request-date"
              onChange={(value) => setForm({ ...form, request_date: value })}
              value={form.request_date}
            />
            <TypedDateInput
              data-testid="permit-expires-date"
              disabled={form.permit_type === "Permit" && form.does_not_expire}
              onChange={(value) => setForm({ ...form, expires_on: value })}
              value={form.permit_type === "Permit" && form.does_not_expire ? "" : form.expires_on}
            />
            {form.permit_type === "Permit" ? (
              <div className="project-permit-received" data-testid="permit-received-block">
                <TypedDateInput
                  data-testid="permit-received-date"
                  onChange={(value) => setForm({ ...form, received_at: value })}
                  value={form.received_at}
                />
                <button
                  className="board-refresh-button project-permit-mark-received"
                  data-testid="permit-mark-received"
                  onClick={() => setForm({ ...form, received_at: todayISO() })}
                  type="button"
                >
                  Mark received today
                </button>
                <label className="master-editor-check-label">
                  <input
                    checked={Boolean(form.does_not_expire)}
                    data-testid="permit-does-not-expire"
                    onChange={(e) => setForm({ ...form, does_not_expire: e.target.checked, expires_on: e.target.checked ? "" : form.expires_on })}
                    type="checkbox"
                  />
                  <span>This permit does not expire</span>
                </label>
              </div>
            ) : (
              <select
                data-testid="permit-status-select"
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                value={form.status}
              >
                {!LOCATE_CONDITIONS.includes(form.status) && (
                  <option value={form.status}>{formatInformationalStatus(form.status)}</option>
                )}
                <option value="valid">Valid</option>
                <option value="trouble">Trouble</option>
                <option value="remark">Remark</option>
              </select>
            )}
            <textarea
              data-testid="permit-notes"
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Office notes"
              value={form.notes}
            />
            {form.permit_type === "Locate / 811" && <><label className="permit-ticket-not-needed-control" htmlFor="permit-ticket-not-needed">
              <input data-testid="permit-ticket-not-needed" id="permit-ticket-not-needed" checked={form.ticket_not_needed} onChange={(e) => setForm({ ...form, ticket_not_needed: e.target.checked })} type="checkbox" />
              Locate / 811 ticket not needed
            </label>
            <textarea data-testid="permit-ticket-description" onChange={(e) => setForm({ ...form, ticket_description: e.target.value })} placeholder="What this Locate / 811 record covers or why it is not needed" value={form.ticket_description} /></>}
            <button
              className="invoice-primary-button"
              data-testid="permit-save-button"
              type="submit"
            >
              {editingId ? "Save changes" : `Add ${form.permit_type}`}
            </button>
            {editingId && (
              <button
                data-testid="permit-cancel-edit"
                onClick={() => {
                  setEditingId("");
                  setForm(emptyForm);
                }}
                type="button"
              >
                Cancel
              </button>
            )}
          </form>
        )}
        <h2 className="workspace-section-title" data-testid="permits-current-heading">Current permits & locates</h2>
        <div className="workspace-grid" data-testid="permits-locates-list">
          {newestRecordsFirst(
            activeRecords.filter(
              (record) => !(record.permit_type === "Locate / 811" && record.status === "expired"),
            ),
          ).map((record) => (
            <article
              className={`project-note-entry ${record.id === targetPermitId ? "permit-record-focused" : ""}`}
              data-testid={`permit-record-${record.id}`}
              key={record.id}
            >
              <strong>
                {record.permit_type} ·{" "}
                {record.reference_number || "No reference"}
              </strong>
              <p>
                {record.project_code}
                {record.project_name !== record.project_code &&
                  ` · ${record.project_name}`}
                {" · expires "}
                {record.expires_on || "not set"}
              </p>
              {record.ticket_not_needed && <p data-testid={`permit-ticket-not-needed-label-${record.id}`}>Ticket not needed · {record.ticket_description || "No description"}</p>}
              {record.permit_type === "Permit" ? (
                <PermitReadinessLabel
                  reason={record.calculated_reason}
                  state={record.calculated_state}
                  testId={`permit-status-badge-${record.id}`}
                />
              ) : (
                <div className="permit-locate-conditions">
                  <span className="permit-locate-human" data-testid={`permit-condition-badge-${record.id}`}>
                    Condition: <InformationalStatusText label={record.status} />
                  </span>
                  <LocateReadinessLabel
                    reason={record.calculated_reason}
                    state={record.calculated_state}
                    testId={`permit-status-badge-${record.id}`}
                  />
                </div>
              )}
              <Link to={`/projects/${record.project_id}`}>Open project</Link>
              {canManageRecords && (
                <div className="communication-filters">
                  <button
                    data-testid={`permit-edit-${record.id}`}
                    onClick={() => edit(record)}
                    type="button"
                  >
                    Edit
                  </button>
                  {record.active !== false && (
                    <button
                      data-testid={`permit-close-${record.id}`}
                      onClick={() => close(record)}
                      type="button"
                    >
                      Close
                    </button>
                  )}
                </div>
              )}
            </article>
          ))}
        </div>
        <ExpiredLocatesFolder
          canManageRecords={canManageRecords}
          close={close}
          edit={edit}
          records={newestRecordsFirst(
            activeRecords.filter(
              (record) => record.permit_type === "Locate / 811" && record.status === "expired",
            ),
          )}
          targetPermitId={targetPermitId}
        />
        <h2 className="workspace-section-title" data-testid="permits-history-heading">History</h2>
        <div className="workspace-grid" data-testid="permits-locates-history-list">
          {newestRecordsFirst(historyRecords).map((record) => (
            <article className={`project-note-entry ${record.id === targetPermitId ? "permit-record-focused" : ""}`} data-testid={`permit-history-record-${record.id}`} key={record.id}>
              <strong>{record.permit_type} · {record.reference_number || "No reference"}</strong>
              <p>{record.project_code} · expires {record.expires_on || "not set"}</p>
              <InformationalStatusText
                label={record.status}
                testId={`permit-history-status-${record.id}`}
              />
              {record.deleted_at && (
                <p data-testid={`permit-retained-delete-${record.id}`}>
                  Deleted from active operations · retained in history
                </p>
              )}
              <Link to={`/projects/${record.project_id}`}>Open project</Link>
              {canManageRecords && <button data-testid={`permit-edit-history-${record.id}`} onClick={() => edit(record)} type="button">Edit</button>}
            </article>
          ))}
        </div>
      </section>
      <Toaster richColors position="bottom-right" />
    </BoardWorkspace>
  );
}

function ExpiredLocatesFolder({ canManageRecords, close, edit, records, targetPermitId }) {
  const [open, setOpen] = useState(false);
  if (!records.length) return null;
  return (
    <details
      data-testid="permits-expired-locates-folder"
      onToggle={(event) => setOpen(event.target.open)}
      open={open}
      style={{ marginTop: "1rem" }}
    >
      <summary
        data-testid="permits-expired-locates-summary"
        style={{
          cursor: "pointer",
          fontSize: "0.9rem",
          fontWeight: 600,
          color: "#6b7280",
          padding: "0.4rem 0",
        }}
      >
        Expired Locates ({records.length}) — {open ? "hide" : "show"}
      </summary>
      <div
        className="workspace-grid"
        data-testid="permits-expired-locates-list"
        style={{ marginTop: "0.5rem" }}
      >
        {records.map((record) => (
          <article
            className={`project-note-entry ${record.id === targetPermitId ? "permit-record-focused" : ""}`}
            data-testid={`permit-expired-record-${record.id}`}
            key={record.id}
          >
            <strong>
              {record.permit_type} · {record.reference_number || "No reference"}
            </strong>
            <p>
              {record.project_code}
              {record.project_name !== record.project_code && ` · ${record.project_name}`}
              {" · expired "}
              {record.expires_on || "not set"}
            </p>
            {record.ticket_not_needed && (
              <p data-testid={`permit-expired-ticket-not-needed-label-${record.id}`}>
                Ticket not needed · {record.ticket_description || "No description"}
              </p>
            )}
            <InformationalStatusText
              label={record.status}
              testId={`permit-expired-status-badge-${record.id}`}
            />
            <Link to={`/projects/${record.project_id}`}>Open project</Link>
            {canManageRecords && (
              <div className="communication-filters">
                <button
                  data-testid={`permit-expired-edit-${record.id}`}
                  onClick={() => edit(record)}
                  type="button"
                >
                  Edit
                </button>
                <button
                  data-testid={`permit-expired-close-${record.id}`}
                  onClick={() => close(record)}
                  type="button"
                >
                  Close
                </button>
              </div>
            )}
          </article>
        ))}
      </div>
    </details>
  );
}
